// My Assignments — employee self view (Phase 2): today + upcoming work, log a
// new assignment (self-create types), check in / check out from the web.
// KaziPeople global theme tokens; the API self-narrows every call.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Briefcase, LogIn, LogOut, Plus } from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { ListWorkbench, LoadMore, sumStatusCounts, totalStatusCount, useDebouncedValue } from '@/features/shared/components/ListWorkbench';
import { getApiErrorMessage } from '@/lib/apiError';
import { getMyProfile } from '@/lib/hrApi';
import {
  WorkAssignment, checkIn, checkOut, listMyAssignments, myAssignmentStatusCounts,
} from '@/services/workAssignmentService';
import { MyFieldDay, myFieldDays } from '@/services/attendanceService';
import {
  ApprovalBadge, ASSIGN_STATUS_FILTERS, ContextLine, CreateAssignmentDialog, SourceChip, StatusBadge, WorkedHoursLine, fmtDT,
} from './shared';
import { OfficeCheckInCard } from './OfficeCheckInCard';

const PAGE = 20;
const ACTIVE_KEY = 'scheduled,in_progress';

export const MyAssignmentsPage: React.FC = () => {
  const { t } = useTranslation('admin');
  const [list, setList] = useState<WorkAssignment[]>([]);
  const [selfId, setSelfId] = useState<string | undefined>();
  const [openCreate, setOpenCreate] = useState(false);
  const [q, setQ] = useState('');
  const qDebounced = useDebouncedValue(q, 300);
  const [status, setStatus] = useState(ACTIVE_KEY);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [counts, setCounts] = useState<Record<string, number>>({});
  // Bumped whenever assignments CHANGE. The list and the status-chip counts are
  // two separate requests; without this only the list was refetched, so the
  // chips kept yesterday's numbers until the page was reloaded — a cancelled
  // assignment vanished from the list while "Scheduled" still counted it.
  const [dataVersion, setDataVersion] = useState(0);
  const refresh = useCallback(() => setDataVersion((n) => n + 1), []);

  const baseParams = useMemo(
    () => ({ limit: PAGE, ...(status ? { status } : {}), ...(qDebounced ? { q: qDebounced } : {}) }),
    [status, qDebounced],
  );

  const load = useCallback(() => {
    listMyAssignments({ ...baseParams, offset: 0 })
      .then((rows) => { setList(rows); setHasMore(rows.length >= PAGE); })
      .catch((e) => toast.error(getApiErrorMessage(e, 'Could not load your assignments.')));
  }, [baseParams]);
  useEffect(load, [load, dataVersion]);
  useEffect(() => {
    getMyProfile().then((p: any) => setSelfId(p?.id)).catch(() => undefined);
  }, []);
  // Status-chip counts — depend on search, not the active status.
  useEffect(() => {
    let cancelled = false;
    myAssignmentStatusCounts(qDebounced ? { q: qDebounced } : {})
      .then((c) => { if (!cancelled) setCounts(c); })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [qDebounced, dataVersion]);

  // What attendance made of my checked-out field days: the overtime each one
  // raised and where it stands. Best-effort — the hours still show without it.
  const [fieldDays, setFieldDays] = useState<Record<string, MyFieldDay>>({});
  const completedIds = useMemo(
    () => list.filter((a) => a.status === 'completed' && a.workedMinutes != null).map((a) => a.id).join(','),
    [list],
  );
  useEffect(() => {
    if (!completedIds) return;
    let cancelled = false;
    myFieldDays(completedIds.split(','))
      .then((rows) => {
        if (cancelled) return;
        const byId: Record<string, MyFieldDay> = {};
        rows.forEach((r) => { byId[r.assignment_id] = r; });
        setFieldDays(byId);
      })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [completedIds, dataVersion]);

  const loadMore = () => {
    setLoadingMore(true);
    listMyAssignments({ ...baseParams, offset: list.length })
      .then((rows) => { setList((prev) => [...prev, ...rows]); setHasMore(rows.length >= PAGE); })
      .catch((e) => toast.error(getApiErrorMessage(e, 'Could not load your assignments.')))
      .finally(() => setLoadingMore(false));
  };

  const act = async (fn: (id: string) => Promise<WorkAssignment>, id: string, ok: string) => {
    try {
      await fn(id);
      toast.success(ok);
      refresh();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Action failed.'));
    }
  };

  const Row: React.FC<{ a: WorkAssignment; actions?: boolean }> = ({ a, actions }) => (
    <div className="flex items-center justify-between gap-3 border-b border-border py-3 px-4">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-foreground truncate">{a.title}</p>
          <SourceChip a={a} />
        </div>
        <ContextLine a={a} />
        <p className="text-xs text-muted-foreground tabular-nums">
          {a.status === 'in_progress'
            ? `${t('wa.started', { defaultValue: 'Started' })} ${fmtDT(a.actualStartAt)}`
            : a.status === 'completed' && a.actualStartAt && a.actualEndAt
              ? `${fmtDT(a.actualStartAt)} → ${fmtDT(a.actualEndAt)}`
              : `${fmtDT(a.plannedStartAt)} → ${fmtDT(a.plannedEndAt)}`}
        </p>
        {a.status === 'completed' && <WorkedHoursLine a={a} fieldDay={fieldDays[a.id]} />}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <StatusBadge status={a.status} />
        <ApprovalBadge status={a.approvalStatus} />
        {actions && a.status === 'scheduled' && (
          <Button size="sm" onClick={() => act(checkIn, a.id, t('wa.checked_in', { defaultValue: 'Checked in.' }) as string)}>
            <LogIn className="w-4 h-4 mr-1" />{t('wa.check_in', { defaultValue: 'Check in' })}
          </Button>
        )}
        {actions && a.status === 'in_progress' && (
          <Button size="sm" variant="outline"
            onClick={() => act(checkOut, a.id, t('wa.checked_out', { defaultValue: 'Checked out — assignment completed.' }) as string)}>
            <LogOut className="w-4 h-4 mr-1" />{t('wa.check_out', { defaultValue: 'Check out' })}
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <div className="p-6 space-y-4 max-w-3xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-h2 text-charcoal flex items-center gap-2">
            <Briefcase className="w-5 h-5" />
            {t('wa.my_title', { defaultValue: 'My Assignments' })}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t('wa.my_sub', { defaultValue: 'Your client visits, field work and travel — check in when you start, out when you finish.' })}
          </p>
        </div>
        {selfId && (
          <Button onClick={() => setOpenCreate(true)}>
            <Plus className="w-4 h-4 mr-1" />{t('wa.log', { defaultValue: 'Log assignment' })}
          </Button>
        )}
      </div>
      <OfficeCheckInCard />
      <ListWorkbench
        search={q}
        onSearch={setQ}
        searchPlaceholder={t('wa.search_ph', { defaultValue: 'Search assignments…' }) as string}
        statuses={ASSIGN_STATUS_FILTERS.map((s) => ({ key: s.key, label: t(`wa.status_${s.key.replace(/,/g, '_')}`, { defaultValue: s.label }) as string, count: sumStatusCounts(counts, s.key) }))}
        activeStatus={status}
        onStatus={setStatus}
        allLabel={t('common.all', { defaultValue: 'All' }) as string}
        allCount={totalStatusCount(counts)}
      />
      <Card><CardContent className="p-0">
        {list.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground">
            {q || status !== ACTIVE_KEY
              ? t('wa.my_no_match', { defaultValue: 'No assignments match your filters.' })
              : t('wa.my_none', { defaultValue: 'No active or upcoming assignments. Working off-site? Log it so your presence is recorded.' })}
          </p>
        ) : list.map((a) => <Row key={a.id} a={a} actions={['scheduled', 'in_progress'].includes(a.status)} />)}
      </CardContent></Card>
      <LoadMore
        shown={list.length}
        hasMore={hasMore}
        loading={loadingMore}
        onMore={loadMore}
        moreLabel={t('common.load_more', { defaultValue: 'Load more' }) as string}
        countLabel={(s) => t('wa.showing', { defaultValue: `${s} shown`, count: s }) as string}
      />
      {selfId && (
        <CreateAssignmentDialog open={openCreate} onClose={() => setOpenCreate(false)}
          onCreated={refresh} selfEmployeeId={selfId} />
      )}
    </div>
  );
};

export default MyAssignmentsPage;
